/**
 * Tour content — DATA ONLY.
 *
 * Nothing here imports React or touches the DOM. Editing, re-ordering or
 * re-wording a tour means editing this file and nothing else.
 *
 * A step's `label` is BOTH the copy anchor and the lookup key: it is matched
 * against the sidebar entry's own accessible label. Keep it identical to the
 * text the sidebar renders, or the step is skipped.
 */

import {
  HOST,
  type AnchorSpec,
  sidebarAnchor,
  sidebarCardAnchor,
  submenuAnchor,
} from './engine/anchors';

export interface TourStep {
  /** Stable id for this step, used in progress and diagnostics. */
  id: string;
  title: string;
  /**
   * Body copy.
   *
   * 🔴 driver.js assigns both this and `title` with `innerHTML`, so keep them
   * PLAIN PROSE. Never interpolate anything a user or an API supplied, and write
   * `and` rather than a bare `&` in copy that could be followed by a letter (an
   * accidental `&amp;`-shaped run would render as one character).
   */
  description: string;
  /**
   * Short label for the stage tracker's step list.
   *
   * The popover title is a sentence ("Inbox — every conversation in one place");
   * a 244px-wide tracker row needs "Inbox". Set automatically by `moduleStep` and
   * `submodules` from the label they already have, so it cannot drift from the
   * copy — and it is a real field rather than a title split on "—" or "›", which
   * would silently produce nonsense the first time a title used either character
   * for anything else.
   */
  navLabel?: string;
  anchor: AnchorSpec;
  side?: 'top' | 'right' | 'bottom' | 'left' | 'over';
  /**
   * Sequencing cluster. Lower groups run first; `order: 'dom'` sorts by
   * on-screen position only WITHIN a group. Defaults to 0.
   *
   * 🔴 Without this, `order: 'dom'` would sequence purely by geometry — and the
   * header sits above the sidebar, so a step anchored to a header control
   * (Nexa AI at y≈11) would sort in front of Overview (y≈80) and the tour would
   * no longer start where it promises to. Groups let a tour say "these stops
   * come after those ones" without giving up automatic ordering inside each.
   */
  group?: number;
  /**
   * Id of the module step this step belongs under. Set on SUBMODULE steps.
   *
   * 🔴 A step with this set is NOT part of the run when the tour starts. Its
   * module's stop offers an "Explore submodules" button, and only pressing that
   * splices the children in — immediately after their parent, in authored order.
   * Opt-in rather than automatic because the full walk is 17 stops and every
   * sub-flow expanded would be 55: enough that most people would abandon the
   * tour before reaching the modules they actually came for.
   *
   * 🔴 Children are NOT sequenced by geometry the way their parents are. A
   * submodule row does not exist in the DOM until its group is open, so it cannot
   * be measured when the run is assembled — authored order is used instead, which
   * is also the order the sidebar renders them.
   *
   * A child can never outlive its parent: with no module row on screen there is
   * no group to open and no button to press.
   */
  parentStepId?: string;
  /**
   * Label of the module whose group must be OPEN for this step to resolve. Set on
   * SUBMODULE steps only — the module's own stop is described with the group still
   * closed, which is the state the user is looking at when they decide whether to
   * open it.
   *
   * The engine needs a label rather than the parent's id because what it opens is
   * a row in the host's sidebar, found the same way every other step finds its
   * target. `submodules()` fills it in from the module label passed once, so there
   * is a single authoring site and the two cannot drift apart.
   */
  requiresModule?: string;
}

export interface TourStage {
  id: string;
  label: string;
  /** Step ids in this stage. Ids, not indices — inserting a step above cannot shift them. */
  stepIds: string[];
}

export interface Tour {
  id: string;
  label: string;
  /** Shown on the launcher. */
  description: string;
  stages: TourStage[];
  steps: TourStep[];
  /**
   * How the run is sequenced.
   *
   * `'authored'` (default) follows the order of `steps` — right for a narrative
   * tour where one stop explains the next.
   *
   * `'dom'` sorts the resolved steps by their position on the page. Correct for
   * a tour that walks a menu: the user is reading a list, so a run that visits
   * it out of order reads as a bug (this tour once went Data Store → Ads →
   * back up to Reports). With `'dom'`, reordering or hiding a menu entry
   * reorders the tour automatically and the two can never disagree.
   */
  order?: 'authored' | 'dom';
}

/**
 * Helper for the common case: one stop per sidebar module.
 *
 * `label` is the sidebar text to look for. Pass an ARRAY to tolerate a rename,
 * current name first — the sidebar's "AI" became "AI Agents", which silently
 * dropped that step until an alias was added.
 *
 * The `nav-<id>` hook is an optional explicit anchor. Passing it costs nothing
 * today (no such attribute exists in the app) and takes precedence
 * automatically the day someone adds it — the documented upgrade path off label
 * matching, for a step where exactness matters more than zero coupling.
 */
function moduleStep(
  id: string,
  label: string | string[],
  title: string,
  description: string,
): TourStep {
  return {
    id,
    title,
    description,
    navLabel: Array.isArray(label) ? label[0] : label,
    anchor: sidebarAnchor(label, `nav-${id}`),
    side: 'right',
  };
}

/**
 * One entry per submodule row: `[label, description, titleOverride?]`.
 *
 * `label` is matched against the row's own accessible label and must be EXACT —
 * a guessed label resolves to nothing and the stop is silently dropped. The list
 * currently in the sidebar was read off the DOM rather than from the menu source,
 * so it reflects what a user actually sees.
 */
type SubmoduleSpec = [label: string | string[], description: string, title?: string];

/**
 * Sub-steps for one module's group.
 *
 * Every derived field comes from the two arguments, so authoring a sub-flow is a
 * list of labels and copy:
 *  - the anchor is scoped to this module's own panel (submodule labels are NOT
 *    unique — "Overview" belongs to Appointments AND Commerce, and "Automations"
 *    is both a module and one of its own rows),
 *  - `parentStepId` is what keeps these out of the run until the user asks,
 *  - `requiresModule` is what tells the engine which group to open,
 *  - the title is a breadcrumb, so a stop inside a sub-flow says where it is.
 */
function submodules(
  parentStepId: string,
  moduleLabel: string | string[],
  items: SubmoduleSpec[],
): TourStep[] {
  const labels = Array.isArray(moduleLabel) ? moduleLabel : [moduleLabel];
  const moduleName = labels[0];

  return items.map(([label, description, title]) => {
    const subName = Array.isArray(label) ? label[0] : label;
    return {
      // Prefixed with the parent, because sub labels repeat across modules.
      id: `${parentStepId}--${subName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`,
      title: title ?? `${moduleName} › ${subName}`,
      description,
      navLabel: subName,
      anchor: submenuAnchor(labels, label),
      side: 'right',
      parentStepId,
      requiresModule: moduleName,
    };
  });
}

const MODULE_STEPS: TourStep[] = [
  moduleStep(
    'overview',
    'Overview',
    'Overview — your starting point',
    'The home dashboard. It summarises the whole workspace at a glance: how many conversations came in, what your campaigns delivered, how leads are moving through the pipeline, and where revenue is coming from. Use it to spot what needs attention, then jump straight into that module.',
  ),
  moduleStep(
    'inbox',
    'Inbox',
    'Inbox — every conversation in one place',
    'A shared team inbox for WhatsApp, Instagram and Facebook. Reply, send templates and media, assign a chat to a teammate, set its priority, and filter by unread or assignee. Everything a customer has ever said to you lives on one thread.',
  ),
  /*
    🔴 NO 'Campaigns' MODULE STOP. The whole `marketing` group — Message Templates
    AND WhatsApp Campaigns — is hidden, because `new-campaigns` below now carries
    both of them as tabs on one screen. A stop anchored to a sidebar group that no
    longer renders matches nothing and is silently dropped from the run, so the only
    thing keeping it here would be a tracker row promising a module the visitor
    cannot see.

    Its two submodule stops and their two guided flows went with it — see the
    `submodules('campaigns', …)` note further down and `tasks/index.ts`. Re-add all
    four together if the group comes back.
  */
  /**
   * The Campaigns/Templates REDESIGN — now the ONLY campaigns stop, since the
   * `marketing` group it was previewing alongside is hidden (see the note above).
   *
   * It carries no submodule sub-flow — its two halves are TABS inside its own
   * page, not sidebar rows, so they cannot be tour stops (a step's anchor has to
   * resolve when the run is assembled, and an in-page tab does not exist until
   * something has navigated there). They are walked by this stop's guided flow
   * instead — see `tasks/newCampaignsModule.ts`, which is unaffected by the
   * removals above: it navigates to `/home/new-campaigns` itself and rings the
   * page's own tabs and drawers, never a sidebar row.
   */
  moduleStep(
    'new-campaigns',
    /*
      🔴 THE SIDEBAR ROW READS "Campaigns", NOT "New Campaigns". The group's id
      is `new-campaigns` but its `title` is 'Campaigns' (AppSidebar.tsx) — it
      took over the name when the old `marketing` group was hidden at the
      redesign cutover. Structural anchors compare the accessible label with
      `===` (engine/anchors.ts), so asking for "New Campaigns" matched nothing
      and this stop was silently dropped from every run: no tour stop, no
      "Talk to customers" tracker row for it, and no guided-flow button, because
      `getTaskForStep('new-campaigns')` is only ever consulted for a step that
      resolved. Alias array, CURRENT NAME FIRST — `navLabel` takes [0], so the
      tracker row reads the word that is actually in the sidebar.
    */
    ['Campaigns', 'New Campaigns'],
    'Campaigns — reach many people at once',
    'Build and send WhatsApp broadcasts, and write the approved templates they go out on — one screen with two tabs: Campaigns for the sends and Templates for the wording behind them. Pick an audience, schedule the send, then watch delivery, reads and replies land per campaign; replies come back into the Inbox as ordinary conversations. Try it below and the walkthrough takes one tab at a time: it rings Campaigns and offers to build a send, then rings Templates and offers to write the wording behind it.',
  ),
  moduleStep(
    'crm',
    'CRM',
    'CRM — leads, deals and tickets',
    'Your customer database as a drag-and-drop board. Move leads through pipeline stages, record deal values, log notes and activities, and raise support tickets. Every contact links back to their conversation in the Inbox.',
  ),
  moduleStep(
    'automations',
    'Automations',
    'Automations — let the system reply for you',
    'A visual flow builder. Trigger on a keyword, a form submission, an abandoned cart or a schedule, then chain actions: send a message, wait, branch on a condition, collect a payment, or hand off to a human. Forms and their submissions are here as well.',
  ),
  moduleStep(
    'social-media',
    'Social Media',
    'Social Media — publish and schedule posts',
    'Create posts once and publish them to Instagram, Facebook, LinkedIn and YouTube. Schedule ahead on a calendar, review what is queued, and see how each post performed after it went out.',
  ),
  moduleStep(
    'instagram',
    'Instagram',
    'Instagram — comments, DMs and insights',
    'Instagram-specific automation: reply to comments on a post, send the commenter a DM, and keep the conversation going with AI grounded in that product. Post performance and account insights sit alongside it.',
  ),
  moduleStep(
    'appointments',
    'Appointments',
    'Appointments — let customers book time',
    'Publish a booking link with your real availability. Customers pick a slot, get a confirmation and a calendar invite, and receive WhatsApp reminders before the meeting. Supports one-to-one slots and fixed-time group sessions.',
  ),
  moduleStep(
    'commerce',
    'Commerce',
    'Commerce — catalogue, orders and payments',
    'Sell without leaving chat. Sync a product catalogue from Shopify or WooCommerce, share products in a conversation, take payments through a link, and track every order from placed to delivered.',
  ),
  moduleStep(
    'data-store',
    'Data Store',
    'Data Store — your own tables',
    'Spreadsheet-style tables you define yourself. Useful for anything the product does not model out of the box — inventory, referral codes, event registrations — and readable and writable from inside an automation flow.',
  ),
  moduleStep(
    'ads',
    'Ads',
    'Ads — Meta campaigns and attribution',
    'Connect a Meta ad account to run and measure click-to-WhatsApp and lead-form ads. See spend, cost per lead, and — because leads land in your CRM — which ads produced customers rather than just clicks.',
  ),
  moduleStep(
    'reports',
    'Reports',
    'Reports — measure what happened',
    'Analytics across the whole workspace: message volume and response times, campaign performance, pipeline conversion, and revenue. Filter by date range and export what you need.',
  ),
  moduleStep(
    'ai',
    ['AI Agents', 'AI'],
    'AI Agents — agents that work for you',
    'Configure AI agents that answer customer questions from your own knowledge base, qualify leads, and take over routine replies — handing off to a person the moment a conversation needs one.',
  ),
  moduleStep(
    'petpooja',
    'Petpooja',
    'Petpooja — connect your restaurant POS',
    'Links a Petpooja point-of-sale to this workspace. Your menu and store open/closed status sync across, orders placed in chat are pushed straight into the POS, and status changes flow back to the customer on WhatsApp automatically.',
  ),
  moduleStep(
    'settings',
    'Account Settings',
    'Account Settings — set the workspace up',
    'Connect WhatsApp and social accounts, invite teammates and control what each of them can see, manage integrations and billing, and review the audit log of everything that has changed.',
  ),
  /*
    🔴 NO 'support' MODULE STOP. The Support accordion is in
    HIDDEN_PRIMARY_MENU_IDS (2026-08-21) — Getting Started / Updates live in
    the header account menu, Contact Support is a button on Getting Started,
    FAQs still resolves as a typed URL. A stop whose sidebar anchor never
    renders is dropped silently, and the tracker would still promise it.
  */
];

/* ------------------------------------------------------------------ *
 * Submodule sub-flows — OPT-IN, one per module that has a group.
 *
 * Reached only from the "Explore submodules" button on the module's own stop, so
 * the default walk stays one stop per rendered sidebar module and nobody is
 * marched through the extra rows they did not ask for.
 *
 * The modules below are the collapsible groups the sidebar still renders.
 * Hidden groups (Support / `help`, Account Settings, Instagram) have no
 * submodule walk. Plain-link modules (Overview, Social Media, CRM, Data Store,
 * Reports, AI Agents) have nothing to expand, so their stops carry no button —
 * the engine decides that by looking for a group on screen, not from a list here.
 * ------------------------------------------------------------------ */

const SUBMODULE_STEPS: TourStep[] = [
  ...submodules('inbox', 'Inbox', [
    [
      'WhatsApp',
      'Every WhatsApp thread for this business account in one shared queue. Reply, send an approved template, attach media, assign the chat to a teammate or set its priority — and the 24-hour messaging window is tracked for you, so you know when a template is required instead of finding out from a failed send.',
    ],
    [
      'Instagram',
      'Instagram DMs and comment-led conversations in the same inbox. A thread opened by a comment remembers the post it came from, so replies stay grounded in the right product rather than the last thing discussed.',
    ],
    [
      'Facebook',
      'Messages from your Facebook Page, alongside everything else. Same reply tools, same assignment and priority, so nobody has to watch a second tab.',
    ],
    [
      'AI Chats',
      'Conversations your AI agents are handling on their own. Read what was said, see the point where the AI handed off to a person, and step in mid-thread whenever you want to take it over yourself.',
    ],
  ]),

  /*
    🔴 NO 'campaigns' SUB-FLOW — 'Message Templates' and 'WhatsApp Campaigns'. Both
    rows belong to the hidden `marketing` group (see the module note above), so both
    anchors resolve to nothing. The redesigned module reaches the same two surfaces
    as TABS, walked by `tasks/newCampaignsModule.ts` rather than by sidebar stops.
  */

  ...submodules('automations', 'Automations', [
    [
      'Automations',
      'The flow list and the visual builder. Start from a keyword, a form submission, an abandoned cart or a schedule, then chain messages, waits, conditions, payments and a handoff to a human.',
      'Automations › Flows',
    ],
    [
      'Forms',
      'Forms you can share as a link or drop into a flow. Submissions land in the CRM as leads and can start an automation the moment they arrive.',
    ],
  ]),

  ...submodules('appointments', 'Appointments', [
    [
      'Overview',
      'Booking activity at a glance — what is coming up, what was booked recently, and how full the calendar is looking.',
    ],
    [
      'Scheduling',
      'Your event types: the kinds of meeting you publish a link for. Set the duration, the fee, how much notice you need, and whether it happens on Zoom, Google Meet, a phone call or in person.',
    ],
    [
      'Meetings',
      'Every booking that has been made, with the invitee, the join link and the option to cancel or reschedule. Cancelling here notifies the invitee for you.',
    ],
    [
      'Contacts',
      'The people who have booked with you, linked back to their CRM record and their conversation history.',
    ],
    [
      'Availability Slots',
      'Your weekly working hours, plus an optional recurring break such as lunch. Anything outside these is never offered to an invitee, so you cannot be booked over your own day.',
    ],
    [
      'Calendar Integrations',
      'Connect Google Calendar, Outlook or Zoom. Once connected, a booking creates a real calendar event and a real meeting link on your own account rather than a placeholder.',
    ],
    [
      'WhatsApp Reminders',
      'Reminders before a meeting, sent on WhatsApp rather than only by email — which is the difference between a booking and someone actually turning up.',
    ],
  ]),

  ...submodules('commerce', 'Commerce', [
    [
      'Overview',
      'Selling at a glance: recent orders, what has been paid for, and what the catalogue is doing.',
    ],
    [
      'Product Catalog',
      'Products synced from Shopify or WooCommerce, or added by hand. Any of them can be shared straight into a conversation for the customer to buy from.',
    ],
    [
      'Orders',
      'Every order from placed to delivered, whether it started in a chat, in an automation or in your store.',
    ],
    [
      'Abandoned Carts',
      'Carts that were started and never paid for. Pair this with an automation and the reminder goes out on its own, once per cart.',
    ],
    [
      'Payments',
      'Payment links you have sent and what became of them, across whichever gateways you have connected.',
    ],
    [
      'Invoices',
      'Invoices raised against orders, showing what has been paid and what is still outstanding.',
    ],
  ]),

  /*
    🔴 NO 'reports' SUB-FLOW. Reports is a single-node sidebar row
    (`isSingleNode`, same as Data Store): clicking it opens `/home/analytics`
    directly. There is no accordion and no "Reports & Analytics" child to
    ring. Sales & Conversions is still a routed page and still maps to the
    `reports` permission id; it is just not a sidebar row.
  */

  /**
   * 🔴 EVERY ROW THE ADS GROUP RENDERS, IN THE ORDER IT RENDERS THEM.
   *
   * This listed TWO of the sidebar's six authored Ads rows — TikTok and Google — so
   * the tour opened the Ads group, said "Includes: TikTok Ads, Google Ads", and
   * walked past Meta Ads and the rest without mentioning them. Meta Ads is the one
   * this product is actually built around, so the omission read as the tour not
   * knowing what the module is for.
   *
   * A submodule step resolves by matching the row's own visible label
   * (`submenuAnchor`), so this list has to stay word-for-word with
   * `AppSidebar.tsx`'s `secondaryItems` for the Ads group. A row renamed there
   * silently drops its stop here — the step is skipped, never an error. When you
   * add or rename an Ads row, edit both, and the parity test in
   * `__tests__/sandbox/adsModuleSubmodules.test.ts` will tell you if you forget.
   *
   * The order matters for a second reason: children are sequenced as AUTHORED, not
   * by geometry (a submodule row cannot be measured before its group is open — see
   * `TourStep.parentStepId`), so an out-of-order list makes the highlight jump
   * around a menu the visitor is reading top to bottom.
   */
  ...submodules('ads', 'Ads', [
    [
      'Meta Ads',
      'The Meta ad account itself: campaigns, ad sets and ads with spend, cost per lead and the CRM outcome of every lead they produced — plus breakdowns by age, placement and region, spend pacing against budget, and creative comparison.',
    ],
    [
      'Google Ads',
      'Google Ads performance here too, so cost per lead can be compared across every channel you buy rather than one report per platform.',
    ],
    /*
      🔴 TWO STOPS, NOT SIX. The sidebar AUTHORS six Ads rows and the demo offers
      only these two, so the other four must not be named here:

        LinkedIn Ads   — off via `sub_features.ads.linkedin_ads: 0`
        TikTok Ads     — off via `DISABLED_FEATURES.tiktok_ads`
        Experiments    — removed from the nav data by `sandbox/nav.ts`
        Competitor Ads — removed from the nav data by `sandbox/nav.ts`

      In every case for the same reason: nothing is seeded behind the page. LinkedIn
      and TikTok can only render a connect-first empty state and there is no way to
      connect anything from inside the sandbox; Competitor Ads searches Meta's live
      public ad library, which no fixture can stand in for; Experiments had its seed
      and its `/ads/experiments*` handlers removed with the row.

      Leaving a stop behind matters more than it looks. An unresolvable step is
      skipped silently, so the run itself would be fine — but `describeStep` builds
      the module's "Includes: …" line from the AUTHORED children, not the resolved
      ones, so the Ads popover would keep promising rows the visitor cannot see. That
      is exactly the stale-entry symptom the 'Short Links' note below records.

      Re-add a stop the moment its row comes back.
    */
  ]),

  ...submodules('petpooja', 'Petpooja', [
    [
      'Overview & Sync',
      'The connection itself: push your menu and store open or closed status across, and check when each last synced.',
      'Petpooja › Overview and sync',
    ],
    [
      'Order History',
      'Orders that moved between WhatsApp and the POS, including what the POS said back about each one.',
    ],
  ]),

  ...submodules('settings', 'Account Settings', [
    [
      'General Settings',
      'Workspace basics — business details, branding, and the AI knowledge base your agents answer customers from.',
    ],
    [
      'Team Management',
      'Invite teammates, set each of their roles, and choose exactly which sections of this sidebar they can open.',
    ],
    [
      'Audit Log',
      'A record of everything that changed: who did it, when, and from where. The first place to look when something is not as you left it.',
    ],
    [
      'Integrations',
      'Connect the rest of the stack — payment gateways, stores, calendars and the POS.',
    ],
    ['Billing', 'Invoices and payment history for your own WeNext subscription.'],
    [
      'Plans & Subscriptions',
      'Your current plan, what it includes, and how to move to a different one.',
      'Account Settings › Plans and subscriptions',
    ],
    [
      'Business Accounts',
      'The WhatsApp Business accounts in this workspace. Add another, or switch which one you are working in — most screens are scoped to the one selected here.',
    ],
    /*
      🔴 NO 'Short Links' STOP. It was removed from the sidebar on 2026-08-11 (the
      route still resolves for a typed URL, but no row renders), so the step matched
      nothing and was silently dropped from every run — the module's own summary line
      still promised it, which is the one visible symptom of a stale entry here.
      Re-add it only if the row comes back.
    */
  ]),

  /*
    🔴 NO 'support' SUB-FLOW. The Support accordion is hidden
    (HIDDEN_PRIMARY_MENU_IDS `help`, 2026-08-21), so every row here would
    match nothing. Getting Started is in the header account menu; Contact
    Support is a button on that page; FAQs still resolves as a typed URL.
  */
];

const NEXA_STEPS: TourStep[] = [
  /* -------------------------------------------------------------- *
   * Nexa AI — two SEPARATE surfaces, deliberately two steps.
   *
   * They are different products behind one name and it is easy to
   * conflate them: the sidebar card opens a full-page workspace where
   * the AI BUILDS the screen for you (GenUI), while the header
   * launcher is a chat assistant that ANSWERS and navigates (Ask AI).
   * `group: 1` keeps both after the module walk — see `TourStep.group`.
   * -------------------------------------------------------------- */
  {
    id: 'nexa-genui',
    group: 1,
    navLabel: 'Nexa AI workspace',
    title: 'Nexa AI workspace — the AI builds the screen',
    description:
      'Opens a full-page AI workspace. Describe what you want in plain language — "show me last week\'s campaign results", "build a flow that chases abandoned carts" — and Nexa assembles the interface and performs the action for you, instead of you hunting for the right page. The toggle switches between the workspace and the normal app.',
    // The icon selector is what keeps this step alive when the sidebar is
    // collapsed — see `sidebarCardAnchor`.
    anchor: sidebarCardAnchor(['Nexa AI', 'Nexa'], 'nexa-genui-card', HOST.nexaCardIcon),
    side: 'right',
  },
  {
    id: 'nexa-ask-ai',
    group: 1,
    navLabel: 'Ask AI',
    title: 'Ask AI — quick answers and shortcuts',
    description:
      'A chat panel for fast guidance without leaving the page you are on. Ask where something lives ("where can I manage contacts?"), what a setting does, or how to get something done, and it answers and can take you straight there. Use the workspace to do the work; use this to find your way around.',
    anchor: { selector: HOST.headerAiLauncher, node: 'nexa-ask-ai-launcher' },
    side: 'bottom',
  },
];

/**
 * The tour's full step set.
 *
 * Order between the three blocks does not sequence the run — `order: 'dom'` sorts
 * the module walk by on-screen position, and children are spliced in after their
 * own parent — so this is grouped for whoever is editing it, not for the engine.
 */
const PRODUCT_TOUR_STEPS: TourStep[] = [...MODULE_STEPS, ...SUBMODULE_STEPS, ...NEXA_STEPS];

export const PRODUCT_TOUR: Tour = {
  id: 'product-tour',
  label: 'Product tour',
  description: 'A guided walk through every module, starting with Overview.',
  // 🔴 Walks the sidebar, so it MUST follow the sidebar. See `Tour.order`.
  order: 'dom',
  steps: PRODUCT_TOUR_STEPS,
  /**
   * Stages are THEMES, and a stage only groups modules that share one.
   *
   * 🔴 EACH STAGE MUST BE CONTIGUOUS IN THE SIDEBAR'S OWN ORDER. A stage is
   * active while the run sits between its first and last position, so two stages
   * that interleave would both report active at once — and the tracker asserts
   * exactly one. That is why a module which fits no theme gets a stage of its
   * own rather than being filed under a neighbouring one: Data Store is custom
   * tables, not part of "Book & sell"; AI Agents answers customers, it does not
   * "Measure & scale"; and Petpooja is a POS integration, not "Account Settings".
   * Each was moved out on exactly that ground.
   *
   * A single-module stage is a normal outcome here, not a placeholder — the
   * tracker renders it identically and its step count simply reads 1.
   *
   * 🔴 A ONE-MODULE STAGE IS LABELLED WITH THE MODULE'S OWN NAME. A stage label
   * exists to name what its modules have in common; with one module there is
   * nothing to summarise, so inventing a theme ("Your own data", "Restaurant
   * POS") makes the tracker and the sidebar say different words for the same
   * thing and the user has to work out that they match. The module name is the
   * word they already know.
   */
  stages: [
    { id: 'start', label: 'Start here', stepIds: ['overview'] },
    {
      id: 'engage',
      label: 'Talk to customers',
      stepIds: ['inbox', 'new-campaigns'],
    },
    { id: 'organise', label: 'Organise & automate', stepIds: ['crm', 'automations'] },
    // Social Media sits under Automations in the sidebar (2026-08-21). Leaving
    // it in `engage` would span CRM + Automations and make two stages active
    // at once. One-module stage → labelled with the module's own name.
    { id: 'social', label: 'Social Media', stepIds: ['social-media'] },
    // Hidden from the sidebar (HIDDEN_PRIMARY_MENU_IDS), so this stage is
    // omitted from the tracker today. Still named so an unhide cannot
    // interleave with Social Media.
    { id: 'instagram', label: 'Instagram', stepIds: ['instagram'] },
    { id: 'operate', label: 'Book & sell', stepIds: ['appointments', 'commerce'] },
    { id: 'data', label: 'Data Store', stepIds: ['data-store'] },
    { id: 'measure', label: 'Measure & scale', stepIds: ['ads', 'reports'] },
    { id: 'ai', label: 'AI Agents', stepIds: ['ai'] },
    { id: 'petpooja', label: 'Petpooja', stepIds: ['petpooja'] },
    { id: 'setup', label: 'Account Settings', stepIds: ['settings'] },
    { id: 'nexa', label: 'Nexa AI', stepIds: ['nexa-genui', 'nexa-ask-ai'] },
  ],
};

/** Every tour this module knows about. Registered by id. */
export const TOURS: Tour[] = [PRODUCT_TOUR];

export function getTour(id: string): Tour | undefined {
  return TOURS.find((t) => t.id === id);
}

/* ------------------------------------------------------------------ *
 * Stage progress — the maths behind the live tracker.
 *
 * Pure and DOM-free so it can be reasoned about (and unit-tested) without a
 * browser. The UI only renders what this returns.
 * ------------------------------------------------------------------ */

export type StageState = 'pending' | 'active' | 'complete';

/** One navigable stop inside a stage, for the tracker's step list. */
export interface StepProgress {
  id: string;
  /** Short label — `navLabel` when the step has one, the full title otherwise. */
  label: string;
  state: StageState;
  /** 0 for a module, 1 for one of its submodules. Drives the indent only. */
  depth: number;
}

export interface StageProgress {
  id: string;
  label: string;
  state: StageState;
  /**
   * The step to jump to when this stage is clicked — its FIRST step that is
   * actually part of this run, which is not necessarily the first one authored
   * (steps get skipped when their target is not on screen).
   */
  entryStepId: string;
  /**
   * How many steps of this stage are in this run RIGHT NOW.
   *
   * Grows when a sub-flow is expanded — those stops genuinely became part of the
   * stage, and reporting the authored count instead would tell a user exploring
   * Inbox's four rows that the stage is 3 steps long.
   */
  stepCount: number;
  /**
   * This stage's stops, in run order, so the tracker can offer a jump to any one
   * of them — including an expanded submodule, which is otherwise reachable only
   * by pressing Next through everything in front of it.
   */
  steps: StepProgress[];
}

export interface TourProgressView {
  stages: StageProgress[];
  /** 1-based position of the active stage, or 0 when nothing is active. */
  activeStage: number;
  /** Total stages that have at least one step in this run. */
  totalStages: number;
}

/**
 * Resolve stage states against the ACTUAL run.
 *
 * 🔴 Driven by `order` — the step ids in the order this run will visit them —
 * not by the authored arrays. The engine skips steps whose target is missing and
 * may re-sequence by on-screen position, so authored indices are the wrong
 * basis: a tracker built on them would report a stage as pending after it had
 * been visited, or count stages the user never sees.
 *
 * A stage with NO steps in this run is omitted entirely, which is what keeps
 * "Stage 2 of 5" honest for a user whose plan hides part of the product.
 *
 * 🔴 A SUBMODULE STEP BELONGS TO ITS PARENT'S STAGE, and that is why this walks
 * the run rather than reading `stage.stepIds`. Children are named in no stage, so
 * the id-based version counted them nowhere: expanding the last module of a stage
 * pushed the run's position past that stage's final index and the tracker marked
 * it COMPLETE while the user was still inside it.
 */
export function computeStageProgress(
  tour: Tour,
  order: string[],
  currentStepId: string | null,
): TourProgressView {
  const positionOf = new Map(order.map((id, i) => [id, i]));
  const current = currentStepId !== null ? positionOf.get(currentStepId) ?? -1 : -1;

  const stepById = new Map(tour.steps.map((s) => [s.id, s]));
  const directStage = new Map<string, string>();
  for (const stage of tour.stages) {
    for (const id of stage.stepIds) directStage.set(id, stage.id);
  }

  /**
   * Which stage owns a step — its own, or its parent's when it is a submodule.
   * The walk is bounded so a malformed tour (a step parented to itself) degrades
   * to "no stage" instead of hanging the render.
   */
  const stageIdOf = (stepId: string): string | undefined => {
    let cursor: string | undefined = stepId;
    for (let hops = 0; cursor !== undefined && hops < 8; hops++) {
      const direct = directStage.get(cursor);
      if (direct) return direct;
      cursor = stepById.get(cursor)?.parentStepId;
    }
    return undefined;
  };

  // Built by walking the run, so positions are already ascending.
  const positionsByStage = new Map<string, number[]>();
  order.forEach((stepId, i) => {
    const stageId = stageIdOf(stepId);
    if (!stageId) return;
    const list = positionsByStage.get(stageId);
    if (list) list.push(i);
    else positionsByStage.set(stageId, [i]);
  });

  const stages: StageProgress[] = [];
  for (const stage of tour.stages) {
    const positions = positionsByStage.get(stage.id);
    if (!positions || positions.length === 0) continue;

    const first = positions[0];
    const last = positions[positions.length - 1];
    let state: StageState = 'pending';
    if (current >= first && current <= last) state = 'active';
    else if (current > last) state = 'complete';

    stages.push({
      id: stage.id,
      label: stage.label,
      state,
      entryStepId: order[first],
      stepCount: positions.length,
      steps: positions.map((position) => {
        const stepId = order[position];
        const step = stepById.get(stepId);
        return {
          id: stepId,
          label: step?.navLabel ?? step?.title ?? stepId,
          state:
            position === current ? 'active' : position < current ? 'complete' : ('pending' as StageState),
          depth: step?.parentStepId ? 1 : 0,
        };
      }),
    });
  }

  const activeStage = stages.findIndex((s) => s.state === 'active') + 1;
  return { stages, activeStage, totalStages: stages.length };
}

/**
 * Development-time sanity check. Returns problems rather than throwing — a
 * malformed tour must never take the app down.
 */
export function validateTours(tours: Tour[] = TOURS): string[] {
  const problems: string[] = [];

  for (const tour of tours) {
    const ids = new Set<string>();
    for (const step of tour.steps) {
      if (ids.has(step.id)) problems.push(`${tour.id}: duplicate step id "${step.id}"`);
      ids.add(step.id);
    }

    const staged = new Set(tour.stages.flatMap((s) => s.stepIds));
    for (const step of tour.steps) {
      // Submodule steps are deliberately in no stage — they inherit their
      // parent's, so listing them would double-count the stage they are already
      // part of. What IS checked is that the parent exists and can be reached.
      if (step.parentStepId) {
        const parent = tour.steps.find((s) => s.id === step.parentStepId);
        if (!parent) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" names a missing parent "${step.parentStepId}"`,
          );
        } else if (parent.parentStepId) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" is nested under another submodule ` +
              `("${parent.id}") — the engine expands one level only`,
          );
        }
        if (!step.requiresModule) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" has no requiresModule, so the engine ` +
              `cannot open the group it lives in`,
          );
        }
        if (staged.has(step.id)) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" is listed in a stage; children inherit ` +
              `their parent's stage and must not be named again`,
          );
        }
        continue;
      }
      if (!staged.has(step.id)) problems.push(`${tour.id}: step "${step.id}" is in no stage`);
    }
    for (const stage of tour.stages) {
      for (const stepId of stage.stepIds) {
        if (!ids.has(stepId)) {
          problems.push(`${tour.id}: stage "${stage.id}" references missing step "${stepId}"`);
        }
      }
    }
  }

  return problems;
}
